import { useCallback, useMemo, type ReactNode } from 'react';
import { Toast } from '@base-ui/react/toast';
import { NotificationContext } from '@/shared/ui/notification/parts/context';
import { NotificationViewport } from '@/shared/ui/notification/parts/viewport';
import { notifyFromOutside, toastManager } from '@/shared/ui/notification/parts/manager';
import type {
  NotificationContextState,
  NotificationOptions,
  NotificationProviderProps,
} from '@/shared/ui/notification/types';

/**
 * The notification provider.
 *
 * Wraps Base UI's `ToastProvider` and exposes the console's own `notify` API on
 * top of it. The indirection is the point: callers say `notify({ tone: 'error',
 * title })` and never touch Base UI's manager, so the toast library stays an
 * implementation detail of this folder.
 *
 * The viewport is rendered INSIDE the provider so a consumer only has to wrap
 * the tree once — there is no second component to remember to mount, and no way
 * to have a provider whose notifications render nowhere.
 */
export function NotificationProvider({ children }: NotificationProviderProps): ReactNode {
  // The hook and the module-scope helper share ONE implementation, so a
  // notification raised from a component and one raised from a loader cannot
  // drift apart in tone, timeout or priority.
  const notify = useCallback((options: NotificationOptions): string => notifyFromOutside(options), []);

  const dismiss = useCallback((id: string) => toastManager.close(id), []);

  const value = useMemo<NotificationContextState>(
    () => ({ notify, dismiss }),
    [notify, dismiss],
  );

  return (
    <Toast.Provider toastManager={toastManager}>
      <NotificationContext.Provider value={value}>
        {children}
        <NotificationViewport />
      </NotificationContext.Provider>
    </Toast.Provider>
  );
}
